(function () {
  'use strict';

  var SKETCHFAB_API_SRC = 'https://static.sketchfab.com/api/sketchfab-viewer-1.12.1.js';
  var apiLoadPromise = null;
  var supportsAbort = typeof window.AbortController === 'function';

  function loadSketchfabApi() {
    if (window.Sketchfab) return Promise.resolve(window.Sketchfab);
    if (apiLoadPromise) return apiLoadPromise;
    apiLoadPromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = SKETCHFAB_API_SRC;
      script.async = true;
      script.onload = function () { resolve(window.Sketchfab); };
      script.onerror = function () { reject(new Error('Sketchfab API failed to load')); };
      document.head.appendChild(script);
    });
    return apiLoadPromise;
  }

  var instances = new WeakMap();

  function initSection(section) {
    if (!section || instances.has(section)) return;
    var iframe = section.querySelector('.product-3d-features__iframe');
    if (!iframe) return;
    var uid = iframe.dataset.uid;
    if (!uid) return;

    var skeleton = section.querySelector('[data-skeleton]');
    var annotationsList = section.querySelector('.product-3d-features__annotations');
    var navPrev = section.querySelector('[data-nav="prev"]');
    var navNext = section.querySelector('[data-nav="next"]');
    var navClose = section.querySelector('[data-nav="close"]');
    var shield = section.querySelector('.product-3d-features__scroll-shield');
    var blocks = Array.prototype.slice.call(section.querySelectorAll('.product-3d-features__annotation'));
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var abortController = supportsAbort ? new AbortController() : null;

    var state = {
      api: null,
      iframe: iframe,
      section: section,
      blocks: blocks,
      activeOrder: -1,
      engaged: false,
      initialCamera: null,
      ready: false,
      pendingAnnotation: null,
      userInteracted: false,
      abortController: abortController,
      observer: null
    };
    instances.set(section, state);

    var ignoreScroll = false;
    var scrollReleaseTimer = 0;
    var settleTimer = 0;
    var scrollSettleTimer = 0;

    /* Helper that wires AbortController.signal into addEventListener options
       when supported. One state.abortController.abort() call later removes
       every listener registered through this. */
    function listen(target, event, handler, extra) {
      var opts = extra || {};
      if (abortController) opts.signal = abortController.signal;
      target.addEventListener(event, handler, opts);
    }

    function indexOfBlock(blockEl) {
      for (var i = 0; i < state.blocks.length; i++) {
        if (state.blocks[i] === blockEl) return i;
      }
      return -1;
    }

    function findBlockByAnnotationIndex(annotationIndex) {
      for (var i = 0; i < state.blocks.length; i++) {
        var n = parseInt(state.blocks[i].dataset.annotationIndex, 10);
        if (n === annotationIndex) return i;
      }
      return -1;
    }

    function applyActive(orderIndex) {
      state.activeOrder = orderIndex;
      for (var i = 0; i < state.blocks.length; i++) {
        var el = state.blocks[i];
        var active = i === orderIndex;
        el.classList.toggle('is-active', active);
        var button = el.querySelector('.product-3d-features__annotation-button');
        if (button) button.setAttribute('aria-expanded', active ? 'true' : 'false');
      }
      updateNavButtons();
    }

    function updateNavButtons() {
      if (!state.blocks.length) return;
      if (navPrev) navPrev.disabled = state.activeOrder <= 0;
      if (navNext) navNext.disabled = state.activeOrder >= state.blocks.length - 1;
    }

    /* Finds the pill whose horizontal center is closest to the carousel's
       visible center. Used by the swipe-driven activation flow on mobile. */
    function findCenteredPillIndex() {
      if (!annotationsList || !state.blocks.length) return -1;
      var center = annotationsList.scrollLeft + annotationsList.clientWidth / 2;
      var closest = -1;
      var closestDist = Infinity;
      for (var i = 0; i < state.blocks.length; i++) {
        var pill = state.blocks[i];
        var pillCenter = pill.offsetLeft + pill.offsetWidth / 2;
        var dist = Math.abs(pillCenter - center);
        if (dist < closestDist) {
          closestDist = dist;
          closest = i;
        }
      }
      return closest;
    }

    /* Snap-settle activation: only fires AFTER the carousel has stopped
       scrolling for 150ms. Activates the centered pill (which moves the
       camera + expands the pill). Only kicks in when the user is already
       engaged — first interaction must be a tap, then subsequent swipes
       navigate. Skipped while ignoreScroll is true (programmatic scrolls). */
    function onAnnotationsScroll() {
      if (!state.engaged || ignoreScroll) return;
      clearTimeout(scrollSettleTimer);
      scrollSettleTimer = setTimeout(function () {
        var closest = findCenteredPillIndex();
        if (closest === -1 || closest === state.activeOrder) return;
        activateOrder(closest);
      }, 150);
    }

    function scrollPillToCenter(pillEl) {
      if (!annotationsList || !pillEl) return;
      if (annotationsList.scrollWidth <= annotationsList.clientWidth + 1) return;

      var pillCenter = pillEl.offsetLeft + pillEl.offsetWidth / 2;
      var targetScrollLeft = pillCenter - annotationsList.clientWidth / 2;

      ignoreScroll = true;
      annotationsList.scrollTo({
        left: targetScrollLeft,
        behavior: reducedMotion ? 'auto' : 'smooth'
      });
      clearTimeout(scrollReleaseTimer);
      scrollReleaseTimer = setTimeout(function () { ignoreScroll = false; }, 500);
    }

    /* Immediate, one-shot capture of the current camera. Used as a fallback
       when the user interacts before the intro animation has settled. */
    function captureInitialCamera() {
      if (!state.api || !state.ready) return;
      try {
        state.api.getCameraLookAt(function (err, view) {
          if (!err && view && !state.initialCamera) state.initialCamera = view;
        });
      } catch (e) { /* noop */ }
    }

    /* Robust capture: polls the camera until the intro animation's zoom
       SETTLES (distance from camera to target stops changing), then captures
       that resting view. This beats a fixed delay because it adapts to the
       model's actual intro length. We compare DISTANCE (not full position)
       so an auto-spinning model — which orbits at a fixed distance — still
       reads as "settled". Stops the moment the user interacts. */
    function captureSettledCamera() {
      if (!state.api || !state.ready) return;
      var lastDist = null;
      var stableCount = 0;
      var sawMovement = false;
      var polls = 0;
      var MAX_POLLS = 40; // ~6s ceiling at 150ms cadence

      function cameraDistance(view) {
        var dx = view.position[0] - view.target[0];
        var dy = view.position[1] - view.target[1];
        var dz = view.position[2] - view.target[2];
        return Math.sqrt(dx * dx + dy * dy + dz * dz);
      }

      function flushPending() {
        if (state.pendingAnnotation === null) return;
        var pending = state.pendingAnnotation;
        state.pendingAnnotation = null;
        gotoAnnotationSafe(pending);
      }

      function poll() {
        if (!instances.has(section) || state.userInteracted || state.initialCamera) return;
        polls++;
        state.api.getCameraLookAt(function (err, view) {
          if (err || !view || !view.position || !view.target) return;
          if (state.userInteracted || state.initialCamera) return;
          var d = cameraDistance(view);
          if (lastDist !== null) {
            var moved = Math.abs(d - lastDist) > Math.max(lastDist * 0.005, 0.0001);
            if (moved) {
              /* Intro animation is in progress — distance is changing. */
              sawMovement = true;
              stableCount = 0;
            } else if (sawMovement) {
              /* We've seen movement before and now it's stable → intro is done. */
              stableCount++;
              if (stableCount >= 2) {
                state.initialCamera = view; // settled at end of intro
                flushPending();
                return;
              }
            }
            /* If we haven't seen movement yet, do nothing — keep polling until
               the intro starts OR we hit MAX_POLLS (in which case there's
               probably no intro and the current view IS the settled view). */
          }
          lastDist = d;
          if (polls < MAX_POLLS) {
            settleTimer = setTimeout(poll, 150);
          } else {
            /* Timed out — either there's no intro animation at all (camera
               has been static the whole time) or the intro is unusually
               long. Either way, take whatever the current view is. */
            state.initialCamera = view;
            flushPending();
          }
        });
      }
      poll();
    }

    function gotoAnnotationSafe(idx) {
      if (Number.isNaN(idx) || idx < 0) return;
      if (!state.ready || !state.api) {
        state.pendingAnnotation = idx;
        return;
      }
      /* If the intro animation hasn't settled yet, DEFER the camera move
         until it has. Otherwise we'd capture a mid-intro state as our
         "home" and close would fly back there. The pill is already
         highlighted by applyActive() — the visual response is instant —
         only the camera move waits the ~1s for the intro to finish.
         captureSettledCamera() will flush this when it captures. */
      if (!state.initialCamera) {
        state.pendingAnnotation = idx;
        return;
      }
      state.userInteracted = true;
      clearTimeout(settleTimer);
      try {
        state.api.gotoAnnotation(idx, {
          preventCameraAnimation: reducedMotion,
          preventCameraMove: false
        });
      } catch (e) { /* noop */ }
    }

    function activateOrder(orderIndex) {
      if (orderIndex < 0 || orderIndex >= state.blocks.length) return;
      applyActive(orderIndex);
      scrollPillToCenter(state.blocks[orderIndex]);
      var idx = parseInt(state.blocks[orderIndex].dataset.annotationIndex, 10);
      if (!Number.isNaN(idx)) gotoAnnotationSafe(idx);
    }

    function engage() {
      if (state.engaged) return;
      state.engaged = true;
      section.classList.add('is-engaged');
      if (shield) shield.setAttribute('tabindex', '-1');
      if (navClose) navClose.removeAttribute('tabindex');
    }

    function disengage() {
      state.engaged = false;
      section.classList.remove('is-engaged');
      if (shield) shield.removeAttribute('tabindex');
      if (navClose) navClose.setAttribute('tabindex', '-1');

      // If a tap was deferred waiting for the intro to settle, cancel it
      state.pendingAnnotation = null;

      state.activeOrder = -1;
      for (var i = 0; i < state.blocks.length; i++) {
        var el = state.blocks[i];
        el.classList.remove('is-active');
        var btn = el.querySelector('.product-3d-features__annotation-button');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      }
      updateNavButtons();

      if (annotationsList && annotationsList.scrollWidth > annotationsList.clientWidth + 1) {
        ignoreScroll = true;
        annotationsList.scrollTo({
          left: 0,
          behavior: reducedMotion ? 'auto' : 'smooth'
        });
        clearTimeout(scrollReleaseTimer);
        scrollReleaseTimer = setTimeout(function () { ignoreScroll = false; }, 500);
      }

      if (state.ready && state.api && state.initialCamera) {
        try {
          state.api.setCameraLookAt(
            state.initialCamera.position,
            state.initialCamera.target,
            reducedMotion ? 0 : 1.5
          );
        } catch (e) { /* noop */ }
      }
    }

    /* Expose timer-clearing on state so destroySection (which runs outside
       this closure) can cancel anything that's queued. */
    state.cancelTimers = function () {
      clearTimeout(scrollReleaseTimer);
      clearTimeout(settleTimer);
      clearTimeout(scrollSettleTimer);
    };

    loadSketchfabApi()
      .then(function (Sketchfab) {
        // Bail if the section was torn down before the API loaded
        if (!instances.has(section)) return;
        var client = new Sketchfab(iframe);
        client.init(uid, {
          success: function (api) {
            if (!instances.has(section)) return;
            state.api = api;
            api.start();
            api.addEventListener('viewerready', function () {
              if (!instances.has(section)) return;
              state.ready = true;
              if (skeleton && skeleton.parentNode) skeleton.parentNode.removeChild(skeleton);

              /* Capture the SETTLED camera position (end of the intro
                 animation), not the one at viewerready (start of the intro,
                 model small/far). Polls until the camera's zoom distance
                 stops changing rather than guessing a fixed delay. */
              captureSettledCamera();
              /* IMPORTANT: only sync our UI state here. Calling activateOrder()
                 would call api.gotoAnnotation(), which makes Sketchfab re-select
                 the annotation, which re-fires annotationSelect → infinite
                 postMessage loop → iframe process gets killed by the browser.
                 Sketchfab already moved its camera by the time this fires. */
              api.addEventListener('annotationSelect', function (index) {
                if (!instances.has(section)) return;
                var order = findBlockByAnnotationIndex(index);
                if (order !== -1) {
                  engage();
                  applyActive(order);
                }
              });
              if (state.pendingAnnotation !== null) {
                var queued = state.pendingAnnotation;
                state.pendingAnnotation = null;
                gotoAnnotationSafe(queued); // may re-defer if intro hasn't settled
              }
            });
          },
          error: function () {
            if (skeleton) skeleton.textContent = 'Could not load 3D model.';
          },
          autostart: iframe.dataset.autostart === 'true' ? 1 : 0,
          ui_infos: 0,
          ui_controls: iframe.dataset.uiControls === 'true' ? 1 : 0,
          ui_annotations: 0,
          annotations_visible: 0,
          // Hide every other Sketchfab UI element so only our pills + nav show
          ui_inspector: 0,
          ui_settings: 0,
          ui_vr: 0,
          ui_ar: 0,
          ui_fullscreen: 0,
          ui_stop: 0,
          ui_watermark: 0
        });
      })
      .catch(function () {
        if (skeleton) skeleton.textContent = 'Could not load 3D viewer.';
      });

    if (shield) {
      listen(shield, 'click', function () { engage(); });
    }

    if (navClose) {
      listen(navClose, 'click', function (e) {
        e.stopPropagation();
        disengage();
      });
    }

    if (annotationsList) {
      listen(annotationsList, 'click', function (e) {
        var button = e.target.closest('.product-3d-features__annotation-button');
        if (!button) return;
        var li = button.closest('.product-3d-features__annotation');
        if (!li) return;
        var order = indexOfBlock(li);
        if (order !== -1) {
          engage();
          activateOrder(order);
        }
      });

      listen(annotationsList, 'scroll', onAnnotationsScroll, { passive: true });
    }

    if (navPrev) {
      listen(navPrev, 'click', function () {
        engage();
        var start = state.activeOrder < 0 ? 1 : state.activeOrder;
        activateOrder(start - 1);
      });
    }
    if (navNext) {
      listen(navNext, 'click', function () {
        engage();
        var start = state.activeOrder < 0 ? -1 : state.activeOrder;
        activateOrder(start + 1);
      });
    }

    updateNavButtons();
    state.observer = observeVisibility(section);
  }

  function observeVisibility(section) {
    if (!window.IntersectionObserver) {
      section.classList.add('is-visible');
      return null;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -10% 0px' });
    observer.observe(section);
    return observer;
  }

  function destroySection(section) {
    var state = instances.get(section);
    if (!state) return;

    // Remove all event listeners attached via listen() in one shot
    if (state.abortController) {
      try { state.abortController.abort(); } catch (e) { /* noop */ }
      state.abortController = null;
    }

    // Disconnect the IntersectionObserver
    if (state.observer) {
      try { state.observer.disconnect(); } catch (e) { /* noop */ }
      state.observer = null;
    }

    // Cancel any in-flight setTimeout callbacks
    if (typeof state.cancelTimers === 'function') {
      try { state.cancelTimers(); } catch (e) { /* noop */ }
      state.cancelTimers = null;
    }

    // Stop the Sketchfab viewer
    if (state.api && typeof state.api.stop === 'function') {
      try { state.api.stop(); } catch (e) { /* noop */ }
    }
    state.api = null;

    instances.delete(section);
  }

  function initAll(root) {
    var scope = root || document;
    var sections = scope.querySelectorAll('.product-3d-features');
    for (var i = 0; i < sections.length; i++) initSection(sections[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor lifecycle
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector('.product-3d-features') || e.target;
    if (section && section.classList && section.classList.contains('product-3d-features')) {
      destroySection(section);
    } else {
      var inner = e.target.querySelector('.product-3d-features');
      if (inner) destroySection(inner);
    }
  });
  document.addEventListener('shopify:block:select', function (e) {
    var li = e.target.closest('.product-3d-features__annotation');
    if (!li) return;
    var section = li.closest('.product-3d-features');
    if (!section) return;
    var state = instances.get(section);
    if (!state) return;
    section.classList.add('is-engaged');
    state.engaged = true;
    for (var i = 0; i < state.blocks.length; i++) {
      if (state.blocks[i] === li) {
        var idx = parseInt(li.dataset.annotationIndex, 10);
        for (var j = 0; j < state.blocks.length; j++) {
          var el = state.blocks[j];
          var active = j === i;
          el.classList.toggle('is-active', active);
          var btn = el.querySelector('.product-3d-features__annotation-button');
          if (btn) btn.setAttribute('aria-expanded', active ? 'true' : 'false');
        }
        state.activeOrder = i;
        // Route through the same safe path used by tap and swipe — queues if
        // Sketchfab isn't ready yet rather than calling into a half-loaded API.
        if (!Number.isNaN(idx)) {
          if (state.ready && state.api) {
            try { state.api.gotoAnnotation(idx); } catch (err) { /* noop */ }
          } else {
            state.pendingAnnotation = idx;
          }
        }
        break;
      }
    }
  });
})();
